import { useEffect, useState, useContext, useRef, useLayoutEffect } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";

import { cookie } from 'shared-kernel/helpers';

import { LayoutContext } from "../layout/";

import Loader from "../loader";
import { getEndpoint } from "../helper";

function useAutoHide(delay) {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!visible) return;

        const timer = setTimeout(() => setVisible(false), delay);
        return () => clearTimeout(timer);
    }, [visible, delay]);

    return [visible, () => setVisible(true)];
}

export default function Product({ }) {
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [variants, setVariants] = useState([]);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    const [showToast, triggerToast] = useAutoHide(2500);

    const { setPageTitle, setCartCount } = useContext(LayoutContext);

    const { id, categoryId } = useParams();
    const slickRef = useRef(null);
    const slickNavRef = useRef(null);
    const cartIdRef = useRef(cookie.get('cid'));
    const shouldHidePriceDataRef = useRef(false);
    const videoRefs = useRef({});
    const [thumbnails, setThumbnails] = useState({});

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await fetch(getEndpoint(`/api/android/product/get?id=${id}&categoryId=${categoryId}`), { signal });
                const json = await response.json();
                setData(json.data);

                setPageTitle(json.data.categoryTitle);

                if (json.data.customProducts.length > 0) {
                    const grouped = groupCustomProducts(json.data.customProducts, json.data.specs);

                    setVariants(grouped);

                    const cheapest = grouped.reduce((min, v) => v.price < min.price ? v : min);

                    setSelectedVariant(cheapest);
                }
                else shouldHidePriceDataRef.current = json.data.status !== 10;

            } catch (err) {
                if (err.name !== "AbortError") setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (!data?.gallery?.length || !slickRef.current) return;

        const $slider = $(slickRef.current);

        $slider.slick({
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            rtl: true,
            infinite: false
        });

        const handleBeforeChange = (event, slick, current, next) => {
            setCurrentSlide(next);
        };

        $slider.on('beforeChange', handleBeforeChange);

        return () => {
            $slider.off('beforeChange', handleBeforeChange);

            if ($slider.hasClass('slick-initialized')) {
                $slider.slick('unslick');
            }
        };
    }, [data]);

    const handleThumbnailClick = idx => {
        $(slickRef.current).slick('slickGoTo', idx);
    };

    const getThumbnailTranslate = () => {
        const total = data.gallery.length;

        if (total <= 3) return '0%';

        if (currentSlide <= 1) {
            return '0%';
        }

        if (currentSlide >= total - 1) {
            return `${(total - 3) * 33.333}%`;
        }

        return `${(currentSlide - 1) * 33.333}%`;
    };

    const formatNumber = n => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    const groupCustomProducts = (customProducts, specs) => {
        const specMap = {};
        const specValueMap = {};

        specs.forEach(spec => {
            specMap[spec.id] = spec.title;
            spec.specValues.forEach(val => {
                specValueMap[val.id] = val.value;
            });
        });

        const grouped = {};

        customProducts.forEach(item => {
            const {
                customProductId,
                specId,
                specValueId,
                price,
                oldPrice,
                discount,
                status,
                status_
            } = item;

            if (!grouped[customProductId]) {
                grouped[customProductId] = {
                    customProductId,
                    price,
                    oldPrice,
                    discount,
                    status,
                    status_,
                    specs: []
                };
            }

            grouped[customProductId].specs.push({
                specId,
                specValueId,
                title: specMap[specId],
                value: specValueMap[specValueId]
            });
        });

        return Object.values(grouped);
    }

    const handleSpecChange = (specId, value) => {
        if (!variants.length) return;

        const candidates = variants.filter(v =>
            v.specs.some(s => s.specId === specId && s.value === value)
        );

        if (candidates.length === 1) {
            setSelectedVariant(candidates[0]);
            return;
        }

        const best = candidates.reduce(
            (best, v) => {
                const score = v.specs.filter(s =>
                    selectedVariant.specs.some(
                        cs => cs.specId === s.specId && cs.value === s.value
                    )
                ).length;

                return score > best.score ? { v, score } : best;
            },
            { v: candidates[0], score: -1 }
        );

        setSelectedVariant(best.v);
    };

    const CustomProducts = () => {
        if (!selectedVariant || data.customProducts.length == 0) return null;

        return (
            <div className="mt-4">
                {data.specs.map(s => (
                    <div key={s.id} className="mt-2">
                        <div className="mb-1">{s.title}</div>

                        <select
                            className="bc-w br-4 bw-1 w-100 py-3 px-2"
                            style={{ borderColor: '#eeeeee' }}
                            value={
                                selectedVariant.specs.find(x => x.specId === s.id)?.value
                            }
                            onChange={e => handleSpecChange(s.id, e.target.value)}
                        >
                            {s.specValues.map(sv => (
                                <option key={sv.id} value={sv.value}>
                                    {sv.value}
                                </option>
                            ))}
                        </select>
                    </div>
                ))}
            </div>
        );
    };

    const handleQuantityChange = action => {
        if (action === '+') setQuantity(Number(quantity) + 1);
        else setQuantity(Math.max(1, Number(quantity) - 1));
    }

    const handleAddToCart = e => {
        if (shouldHidePriceDataRef.current) {
            triggerToast();
            return;
        }

        fetch(getEndpoint("/api/cart/submit"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                cartId: cartIdRef.current,
                productId: id,
                brandId: null,
                customProductId: selectedVariant ? selectedVariant.customProductId : null,
                quantity
            })
        })
            .then(res => res.json())
            .then(data => {
                triggerToast();

                cookie.set('cid', data.data);

                fetch(getEndpoint(`/api/cart/get?cartid=${data.data}`))
                    .then(res => res.json())
                    .then(data => {
                        let quantitySum = data.data.reduce((p, c) => p += c.quantity, 0);

                        setCartCount(quantitySum);
                    });
            });
    }

    const isVideo = fileName => /\.(mp4|webm|ogg|mov)$/i.test(fileName);

    const getThumbnailPath = fileName => {
        if (isVideo(fileName)) return getEndpoint('/img/video_player.png');

        return getEndpoint(`/uploads/shop/product-file/${fileName}`);
    }

    const handleVideoLoaded = (fileName) => {
        const video = videoRefs.current[fileName];

        if (!video || !video.videoWidth) return;

        video.currentTime = 0;

        const canvas = document.createElement("canvas");

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const thumbnail = canvas.toDataURL("image/jpeg");

        setThumbnails((prev) => ({
            ...prev,
            [fileName]: thumbnail,
        }));
    };

    const getGalleryItem = fileName => {
        if (isVideo(fileName)) return (
            <div className='video-preview p-r d-f ai-c'>
                <video
                    ref={(el) => { videoRefs.current[fileName] = el; }}
                    crossOrigin="anonymous"
                    src={getEndpoint(`/uploads/shop/product-file/${fileName}`)}
                    className="d-n"
                    preload="metadata"
                    muted
                    onLoadedData={() => handleVideoLoaded(fileName)} />
                <img src={thumbnails[fileName] || ''} />
            </div>
        );

        return <img src={getEndpoint(`/uploads/shop/product-file/${fileName}`)} />;
    }

    if (loading) return <Loader />;
    if (error) return <p>Error: {error}</p>;

    return (
        <>
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '5rem' }}>
                <div className='bc-w m-2 br-2 p-4'>
                    <div className='fw-600 fs-3'>{data.title}</div>
                    <div>{data.secondTitle}</div>
                    <div dir='rtl' className='mt-4 normal'>
                        <div className='js__slick slick-main' ref={slickRef}>
                            {data.gallery.map((x, i) => (
                                <Link className='d-b' key={i} to={`/product-gallery/${id}/${categoryId}/${i}/${isVideo(x)}`}>
                                    {getGalleryItem(x)}
                                </Link>
                            ))}
                        </div>
                        <div className='ta-c fw-600 fs-3'>
                            {currentSlide + 1}/{data.gallery.length}
                        </div>
                        <div className='o-h'>
                            <div
                                className='d-f thumbnail-container'
                                style={{
                                    transform: `translateX(${getThumbnailTranslate()})`,
                                    transition: 'transform 0.35s ease'
                                }}
                            >
                                {data.gallery.map((x, i) => (
                                    <div
                                        key={i}
                                        className={`thumbnail ${currentSlide === i ? 'active' : ''}`}
                                        onClick={() => handleThumbnailClick(i)}
                                    >
                                        <img src={getThumbnailPath(x)} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <svg className='mt-4' xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32">
                        <path fill='#757575' d="M24 19.906q1.625 0 2.75 1.156t1.125 2.719q0 1.625-1.156 2.781t-2.719 1.156-2.719-1.156-1.156-2.781q0-0.625 0.063-0.875l-9.438-5.5q-1.188 1.063-2.75 1.063-1.625 0-2.813-1.188t-1.188-2.813 1.188-2.813 2.813-1.188q1.563 0 2.75 1.063l9.375-5.438q-0.125-0.625-0.125-0.938 0-1.625 1.188-2.813t2.813-1.188 2.813 1.188 1.188 2.813-1.188 2.813-2.813 1.188q-1.5 0-2.75-1.125l-9.375 5.5q0.125 0.625 0.125 0.938t-0.125 0.938l9.5 5.5q1.125-1 2.625-1z"></path>
                    </svg>
                </div>

                <div className='bc-w m-2 br-2 p-4 my-4'>
                    <div className='d-f'>
                        <div className='w-8'>
                            {data.brandIcon ? <img src={getEndpoint(`/uploads/shop/product-file/${data.brandIcon}`)} /> : <img src={getEndpoint('/pwa/img/no_pic_750x750.jpg')} />}
                        </div>
                        <div className='mr-4 pt-2'>
                            <span style={{ color: '#757575' }}>وضعیت کالا:</span>&nbsp;
                            <span style={{ color: '#4caf50' }}>{selectedVariant ? selectedVariant.status_ : data.status_}</span>
                        </div>
                    </div>
                    <CustomProducts />
                    {
                        !shouldHidePriceDataRef.current && (
                            <>
                                <div className='my-4' style={{ height: '1px', backgroundColor: '#dddddd' }} />
                                {/* Price */}
                                <div className='d-f ai-c px-2'>
                                    <div className='d-f ai-c jc-c _bc-g-d c-w p-1 br-4'>
                                        <div className='d-l fs-4'>{selectedVariant ? selectedVariant.discount : data.discount}%</div>
                                    </div>
                                    <div className='mr-2 fw-600'>
                                        <div className='d-f ai-c mt-1'>
                                            <div className='fs-3' style={{ color: '#9c9c9c', textDecorationLine: 'line-through' }}>{formatNumber(selectedVariant ? selectedVariant.oldPrice : data.oldPrice)}</div>
                                            &nbsp;
                                            <div style={{ color: '#9c9c9c', fontSize: '.8em' }}>ریال</div>
                                        </div>
                                        <div className='d-f ai-c p-r _c-b_b' style={{ top: '-.5em' }}>
                                            <div className='fs-3'>{formatNumber(selectedVariant ? selectedVariant.price : data.price)}</div>
                                            &nbsp;
                                            <div style={{ fontSize: '.8em' }}>ریال</div></div>
                                    </div>
                                </div>

                                {/* Quantity */}
                                <div className='d-f ai-c mt-2'>
                                    <div style={{ color: '#757575' }}>تعداد:</div>
                                    <div style={{ borderColor: '#acacac' }} className='mr-2 fs-3 bw-1 br-3 d-f ai-c w-9'>
                                        <svg className='d-b fsh-0' xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 32 32" onClick={e => handleQuantityChange('+')}>
                                            <path fill='#075e54' d="M25.313 15.781h-8v8h-2.625v-8h-8v-2.625h8v-8h2.625v8h8v2.625z"></path>
                                        </svg>
                                        <input className='ta-c bc-t w-100' type="number" value={quantity} onChange={e => setQuantity(e.target.value)} />
                                        <svg className='d-b fsh-0 mr-a' xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 32 32" onClick={e => handleQuantityChange('-')}>
                                            <path fill='#075e54' d="M25.313 15.781h-18.625v-2.625h18.625v2.625z"></path>
                                        </svg>
                                    </div>
                                </div>
                            </>
                        )
                    }
                </div>

                {data.hasDesc && (
                    <Link className='bc-w m-2 br-2 p-4 mt-4 d-f ai-c c-p' to={`/product-desc/${id}/${categoryId}`}>
                        <div style={{ color: '#757575' }}>توضیحات</div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32" className='mr-a'>
                            <path fill='#757575' d="M20.563 20.594l-1.875 1.875-8-8 8-8 1.875 1.875-6.125 6.125z"></path>
                        </svg>
                    </Link>
                )}

                {data.hasSpec && (
                    <Link className='bc-w m-2 br-2 p-4 mt-4 d-f ai-c c-p' to={`/product-spec/${data.id}`}>
                        <div style={{ color: '#757575' }}>مشخصات کالا</div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32" className='mr-a'>
                            <path fill='#757575' d="M20.563 20.594l-1.875 1.875-8-8 8-8 1.875 1.875-6.125 6.125z"></path>
                        </svg>
                    </Link>
                )}
            </div>
            {
                !shouldHidePriceDataRef.current && (
                    <button type='button' className='p-f b-0 l-0 w-100 c-w _bc-b' style={{ height: '5rem', fontSize: '1.1em' }} onClick={handleAddToCart}>
                        افزودن به سبد خرید
                    </button>
                )
            }
            {
                shouldHidePriceDataRef.current && (
                    <button type='button' className='p-f b-0 l-0 w-100 c-w a:o-n' style={{ height: '5rem', fontSize: '1.1em', backgroundColor: '#ccc' }} onClick={handleAddToCart}>
                        افزودن به سبد خرید
                    </button>
                )
            }

            {showToast && (
                <div className='p-f b-8 l-0 r-0 d-f jc-c'>
                    <div className='d-if px-5 py-3 br-999' style={{ backgroundColor: '#f0f0f0' }}>
                        {shouldHidePriceDataRef.current
                            ? `امکان خرید محصول با وضعیت ${data.status_} وجود ندارد`
                            : "سبد خرید بروزرسانی شد!"
                        }
                    </div>
                </div>
            )}
        </>
    );
}